/**
 * Cookie Consent Context
 * Provides cookie consent state and methods throughout the application
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import {
	getConsent,
	setConsent as setConsentService,
	hasAnalyticsConsent,
	hasMarketingConsent,
	clearConsent,
} from "../services/cookieService";
import { disableAnalytics, initializeAnalytics } from "../firebase";
import { disableSentry, initializeSentry } from "../services/sentryService";

const CookieConsentContext = createContext({
	consentGiven: false,
	consentPreferences: {
		essential: true,
		analytics: false,
		marketing: false,
	},
	showBanner: false,
	showPreferences: false,
	setShowBanner: () => {},
	setShowPreferences: () => {},
	acceptAll: () => {},
	rejectAll: () => {},
	savePreferences: () => {},
	withdrawConsent: () => {},
	hasAnalyticsConsent: () => false,
	hasMarketingConsent: () => false,
});

export const useCookieConsent = () => {
	const context = useContext(CookieConsentContext);
	if (!context) {
		throw new Error("useCookieConsent must be used within a CookieConsentProvider");
	}
	return context;
};

export const CookieConsentProvider = ({ children }) => {
	const [consentGiven, setConsentGiven] = useState(false);
	const [consentPreferences, setConsentPreferences] = useState({
		essential: true,
		analytics: false,
		marketing: false,
	});
	const [showBanner, setShowBanner] = useState(false);
	const [showPreferences, setShowPreferences] = useState(false);

	// Load consent state on mount
	useEffect(() => {
		const consent = getConsent();
		if (consent) {
			setConsentGiven(true);
			setConsentPreferences({
				essential: consent.essential ?? true,
				analytics: consent.analytics ?? false,
				marketing: consent.marketing ?? false,
			});
			setShowBanner(false);
		} else {
			setConsentGiven(false);
			setShowBanner(true);
		}
	}, []);

	// Accept all cookies
	const acceptAll = useCallback(() => {
		const preferences = {
			essential: true,
			analytics: true,
			marketing: true,
		};
		setConsentService(preferences);
		setConsentPreferences(preferences);
		setConsentGiven(true);
		setShowBanner(false);
		setShowPreferences(false);
		// Initialize analytics services
		initializeAnalytics(true);
		initializeSentry(true);
	}, []);

	// Reject all non-essential cookies
	const rejectAll = useCallback(() => {
		const preferences = {
			essential: true,
			analytics: false,
			marketing: false,
		};
		setConsentService(preferences);
		setConsentPreferences(preferences);
		setConsentGiven(true);
		setShowBanner(false);
		setShowPreferences(false);
		disableAnalytics();
		disableSentry();
	}, []);

	// Save custom preferences
	const savePreferences = useCallback((preferences) => {
		const newPreferences = {
			essential: true, // Always true
			analytics: Boolean(preferences?.analytics),
			marketing: Boolean(preferences?.marketing),
		};
		setConsentService(newPreferences);
		setConsentPreferences(newPreferences);
		setConsentGiven(true);
		setShowBanner(false);
		setShowPreferences(false);
		if (newPreferences.analytics) {
			initializeAnalytics(true);
			initializeSentry(true);
		} else {
			disableAnalytics();
			disableSentry();
		}
	}, []);

	// Withdraw consent
	const withdrawConsent = useCallback(() => {
		clearConsent();
		setConsentGiven(false);
		setConsentPreferences({
			essential: true,
			analytics: false,
			marketing: false,
		});
		setShowBanner(true);
		setShowPreferences(false);
		disableAnalytics();
		disableSentry();
	}, []);

	// Check analytics consent
	const checkAnalyticsConsent = useCallback(() => {
		return hasAnalyticsConsent();
	}, []);

	// Check marketing consent
	const checkMarketingConsent = useCallback(() => {
		return hasMarketingConsent();
	}, []);

	const value = {
		consentGiven,
		consentPreferences,
		showBanner,
		showPreferences,
		setShowBanner,
		setShowPreferences,
		acceptAll,
		rejectAll,
		savePreferences,
		withdrawConsent,
		hasAnalyticsConsent: checkAnalyticsConsent,
		hasMarketingConsent: checkMarketingConsent,
	};

	return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
};

export default CookieConsentContext;
